import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setAuth } from '../Components/Redux/authSlice';

import 'bootstrap/dist/css/bootstrap.min.css';
import { FaTrash, FaSearch, FaUserPlus, FaUsers, FaUserShield } from 'react-icons/fa';

const UserComponent: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [userRole, setUserRole] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [paginationInfo, setPaginationInfo] = useState<{
    next: string | null;
    previous: string | null;
    count: number;
  }>({ next: null, previous: null, count: 0 });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState<number | null>(null);
  const dispatch = useDispatch();
  const [authenticated, setAuthenticated] = useState<boolean>(false);

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const token = getCookie('token');
        if (!token) {
          console.error('No token found in cookies');
          dispatch(setAuth(false));
          setError('No token found. Please log in.');
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const usersResponse = await axios.get(
          `/users/all-users/?page=${currentPage}&q=${encodeURIComponent(searchQuery)}`
        );
        setUsers(usersResponse.data.results);
        setPaginationInfo({
          next: usersResponse.data.next,
          previous: usersResponse.data.previous,
          count: usersResponse.data.count,
        });
        await fetchUserRole();
      } catch (err) {
        console.error('Error fetching users:', err);
        handleAxiosError(err);
      } finally {
        setLoading(false);
      }
    };

    const fetchUserRole = async () => {
      try {
        const { data } = await axios.get('/users/user');
        setUserRole(data.user.role);
        setAuthenticated(true);
        console.log('Logged in user role:', data.user.role);
      } catch (error) {
        console.error('Failed to fetch user role:', error);
        setError('Failed to fetch user role');
        setAuthenticated(false);
      }
    };

    fetchUsers();
  }, [dispatch, currentPage, searchQuery]);

  const handleDelete = async (userId: number) => {
    try {
      const token = getCookie('token');
      if (!token) {
        throw new Error('No token found in cookies');
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await axios.delete(`/users/users/${userId}/`);
      setUsers(users.filter(user => user.id !== userId));
      setSuccessMessage('User deleted successfully');
      setError(null);
      setShowDeleteModal(null);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error deleting user:', err);
      handleAxiosError(err);
    }
  };

  const handleAxiosError = (err: any) => {
    if (axios.isAxiosError(err)) {
      if (err.response) {
        setError(`Error: ${err.response.status} ${err.response.statusText}`);
      } else if (err.request) {
        setError('Error: No response received from the server');
      } else {
        setError(`Error: ${err.message}`);
      }
    } else {
      setError('An unexpected error occurred');
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
      case 'super_admin':
        return 'badge bg-gradient-to-r from-purple-500 to-pink-500';
      case 'teacher':
        return 'badge bg-gradient-to-r from-blue-500 to-cyan-500';
      case 'accountant':
        return 'badge bg-gradient-to-r from-green-500 to-teal-500';
      case 'parent':
        return 'badge bg-gradient-to-r from-orange-500 to-red-500';
      default:
        return 'badge bg-secondary';
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 w-100">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading users...</p>
        </div>
      </div>
    );
  }

  if (!authenticated || (userRole !== 'admin' && userRole !== 'super_admin')) {
    return (
      <div className="container-fluid min-vh-100 d-flex justify-content-center align-items-center bg-light">
        <div className="text-center">
          <div className="mb-4">
            <FaUserShield size={64} className="text-danger" />
          </div>
          <h4 className="text-dark mb-3">Access Denied</h4>
          <p className="text-muted mb-4">You don't have the right to view this page.</p>
          <a href="/login" className="btn btn-primary px-4 py-2">
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <div className="container-fluid p-4 min-vh-100 w-100" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        {/* Header Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h2 className="mb-1 fw-bold" style={{ color: '#1a202c' }}>
                <FaUsers className="me-2" style={{ color: '#667eea' }} />
                User Management
              </h2>
              <p className="text-muted mb-0">Manage all system users and their permissions</p>
            </div>
            <a href="/register" className="btn btn-primary px-4 py-2 shadow-sm" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>
              <FaUserPlus className="me-2" />
              Add New User
            </a>
          </div>
        </div>

        {/* Alerts */}
        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show shadow-sm border-0 mb-4" role="alert">
            <strong>Success!</strong> {successMessage}
            <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)}></button>
          </div>
        )}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show shadow-sm border-0 mb-4" role="alert">
            <strong>Error!</strong> {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

        {/* Search Bar */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="input-group">
              <span className="input-group-text bg-white border-end-0">
                <FaSearch className="text-muted" />
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by first name, last name, or email..."
                className="form-control border-start-0 ps-0"
                style={{ fontSize: '15px' }}
              />
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <tr>
                    <th className="py-3 px-4 border-0">First Name</th>
                    <th className="py-3 px-4 border-0">Last Name</th>
                    <th className="py-3 px-4 border-0">Email</th>
                    <th className="py-3 px-4 border-0">Role</th>
                    <th className="py-3 px-4 border-0">Branch</th>
                    <th className="py-3 px-4 border-0 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5">
                        <div className="text-muted">
                          <FaUsers size={48} className="mb-3 opacity-25" />
                          <p className="mb-0">No users found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users.map((user, index) => (
                      <tr key={user.id} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                        <td className="py-3 px-4 align-middle fw-medium">{user.first_name}</td>
                        <td className="py-3 px-4 align-middle fw-medium">{user.last_name}</td>
                        <td className="py-3 px-4 align-middle text-muted">{user.email}</td>
                        <td className="py-3 px-4 align-middle">
                          <span className={getRoleBadgeClass(user.role)} style={{ padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', textTransform: 'capitalize', color: 'black'}}>
                            {user.role.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-3 px-4 align-middle">{user.branch_name}</td>
                        <td className="py-3 px-4 align-middle text-center">
                          <button
                            className="btn btn-sm btn-danger shadow-sm"
                            onClick={() => setShowDeleteModal(user.id)}
                            style={{ padding: '6px 16px', borderRadius: '20px' }}
                          >
                            <FaTrash className="me-1" style={{ fontSize: '12px' }} />
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-3">
          <button
            className="btn btn-outline-primary px-4"
            disabled={!paginationInfo.previous}
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            style={{ borderRadius: '20px' }}
          >
            ← Previous
          </button>
          <span className="fw-medium" style={{ color: '#4a5568' }}>
            Page {currentPage} of {Math.ceil(paginationInfo.count / 10)}
          </span>
          <button
            className="btn btn-outline-primary px-4"
            disabled={!paginationInfo.next}
            onClick={() => setCurrentPage((prev) => prev + 1)}
            style={{ borderRadius: '20px' }}
          >
            Next →
          </button>
        </div>

        {/* Delete Modal */}
        {showDeleteModal !== null && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div className="modal-header border-0" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                  <h5 className="modal-title text-white fw-bold">
                    <FaTrash className="me-2" />
                    Confirm Deletion
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowDeleteModal(null)}></button>
                </div>
                <div className="modal-body p-4">
                  <p className="mb-0" style={{ color: '#4a5568' }}>Are you sure you want to delete this user? This action cannot be undone.</p>
                </div>
                <div className="modal-footer border-0 bg-light">
                  <button
                    type="button"
                    className="btn btn-secondary px-4"
                    onClick={() => setShowDeleteModal(null)}
                    style={{ borderRadius: '20px' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger px-4"
                    onClick={() => handleDelete(showDeleteModal)}
                    style={{ borderRadius: '20px' }}
                  >
                    Delete User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .badge {
            display: inline-block;
          }
          .table-hover tbody tr:hover {
            background-color: #e6e9f0 !important;
            transition: background-color 0.2s ease;
          }
          .btn {
            transition: all 0.2s ease;
          }
          .btn:hover {
            transform: translateY(-1px);
          }
          .card {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }
          .card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 16px rgba(0,0,0,0.1) !important;
          }
        `}</style>
      </div>
    </div>

  );
};

export default UserComponent;